import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, LogOut, Users, Calendar as CalendarIcon, DollarSign, Clock, CheckCircle, AlertCircle, Phone, Mail, MapPin, Package, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast, Toaster } from '../components/ui/sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Calendar Component
const BookingCalendar = ({ bookings, onDateClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    return { daysInMonth, startingDay, year, month };
  };

  const { daysInMonth, startingDay, year, month } = getDaysInMonth(currentDate);
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getBookingsForDate = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(b => b.service_date === dateStr);
  };

  const prevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const today = new Date();
  const isToday = (day) => {
    return today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
  };

  const renderCalendarDays = () => {
    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-gray-50"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayBookings = getBookingsForDate(day);
      const hasBookings = dayBookings.length > 0;
      
      days.push(
        <div 
          key={day} 
          className={`h-24 border border-gray-200 p-1 overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors ${isToday(day) ? 'bg-emerald-50 border-emerald-300' : ''}`}
          onClick={() => onDateClick(day, dayBookings)}
        >
          <div className={`text-sm font-medium mb-1 ${isToday(day) ? 'text-emerald-600' : 'text-gray-700'}`}>
            {day}
          </div>
          {dayBookings.slice(0, 3).map((booking, idx) => (
            <div 
              key={idx} 
              className={`text-xs px-1 py-0.5 rounded mb-0.5 truncate ${
                booking.booking_status === 'completed' ? 'bg-purple-100 text-purple-800' :
                booking.booking_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                'bg-yellow-100 text-yellow-800'
              }`}
            >
              {booking.name}
            </div>
          ))}
          {dayBookings.length > 3 && (
            <div className="text-xs text-gray-500">+{dayBookings.length - 3} more</div>
          )}
        </div>
      );
    }
    
    return days;
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <Button variant="outline" size="sm" onClick={prevMonth}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <h2 className="text-lg font-semibold text-gray-900">
          {monthNames[month]} {year}
        </h2>
        <Button variant="outline" size="sm" onClick={nextMonth}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      
      {/* Day Names */}
      <div className="grid grid-cols-7 bg-gray-100">
        {dayNames.map(day => (
          <div key={day} className="p-2 text-center text-sm font-medium text-gray-600">
            {day}
          </div>
        ))}
      </div>
      
      {/* Calendar Grid */}
      <div className="grid grid-cols-7">
        {renderCalendarDays()}
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 p-3 border-t bg-gray-50">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-yellow-100 border border-yellow-300"></div>
          <span className="text-xs text-gray-600">Pending</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-100 border border-green-300"></div>
          <span className="text-xs text-gray-600">Confirmed</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-purple-100 border border-purple-300"></div>
          <span className="text-xs text-gray-600">Completed</span>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedDateBookings, setSelectedDateBookings] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      await axios.get(`${API}/admin/verify`, { withCredentials: true });
      setIsAuthenticated(true);
      loadDashboardData();
    } catch (error) {
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [statsRes, quotesRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/quotes`, { withCredentials: true }),
        axios.get(`${API}/admin/bookings`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setQuotes(quotesRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
      setIsAuthenticated(true);
      loadDashboardData();
      toast.success('Login successful');
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, { withCredentials: true });
      setIsAuthenticated(false);
      toast.success('Logged out');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API}/admin/quotes/${quoteId}/status?status=${status}`, {}, { withCredentials: true });
      toast.success(`Quote marked as ${status}`);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update quote');
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Are you sure you want to delete this quote?')) return;
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`, { withCredentials: true });
      toast.success('Quote deleted');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to delete quote');
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status?status=${status}`, {}, { withCredentials: true });
      toast.success(`Booking marked as ${status}`);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const updatePaymentStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/payment?status=${status}`, {}, { withCredentials: true });
      toast.success(`Payment marked as ${status}`);
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, { withCredentials: true });
      toast.success('Booking deleted');
      loadDashboardData();
    } catch (error) {
      toast.error('Failed to delete booking');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Toaster />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">Admin Login</CardTitle>
            <p className="text-gray-600">Enter password to access dashboard</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full"
                data-testid="admin-password-input"
              />
              <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="admin-login-btn">
                Login
              </Button>
              <Button type="button" variant="outline" className="w-full" onClick={() => navigate('/')}>
                Back to Website
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      <Toaster />
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="/junkio-logo.png" alt="JUNKio" className="h-10" />
            <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
          <Button variant="outline" onClick={handleLogout} className="flex items-center space-x-2">
            <LogOut className="w-4 h-4" />
            <span>Logout</span>
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Quotes</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.quotes.total}</p>
                    <p className="text-sm text-emerald-600">{stats.quotes.new} new</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-full">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Bookings</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.bookings.total}</p>
                    <p className="text-sm text-emerald-600">{stats.bookings.confirmed} confirmed</p>
                  </div>
                  <div className="bg-emerald-100 p-3 rounded-full">
                    <CalendarIcon className="w-6 h-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Revenue Collected</p>
                    <p className="text-3xl font-bold text-gray-900">${stats.revenue.collected.toFixed(2)}</p>
                    <p className="text-sm text-orange-600">${stats.revenue.pending.toFixed(2)} pending</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Completed Jobs</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.bookings.completed}</p>
                    <p className="text-sm text-gray-500">{stats.bookings.pending} pending</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-full">
                    <CheckCircle className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-emerald-600' : ''}
          >
            Overview
          </Button>
          <Button
            variant={activeTab === 'calendar' ? 'default' : 'outline'}
            onClick={() => setActiveTab('calendar')}
            className={activeTab === 'calendar' ? 'bg-indigo-600' : ''}
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            Calendar
          </Button>
          <Button
            variant={activeTab === 'quotes' ? 'default' : 'outline'}
            onClick={() => setActiveTab('quotes')}
            className={activeTab === 'quotes' ? 'bg-blue-600' : ''}
          >
            <Users className="w-4 h-4 mr-2" />
            Total Quotes ({quotes.length})
          </Button>
          <Button
            variant={activeTab === 'bookings' ? 'default' : 'outline'}
            onClick={() => setActiveTab('bookings')}
            className={activeTab === 'bookings' ? 'bg-emerald-600' : ''}
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            Total Bookings ({bookings.length})
          </Button>
          <Button
            variant={activeTab === 'completed' ? 'default' : 'outline'}
            onClick={() => setActiveTab('completed')}
            className={activeTab === 'completed' ? 'bg-purple-600' : ''}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Completed Jobs ({bookings.filter(b => b.booking_status === 'completed').length})
          </Button>
          <Button
            variant={activeTab === 'revenue' ? 'default' : 'outline'}
            onClick={() => setActiveTab('revenue')}
            className={activeTab === 'revenue' ? 'bg-green-600' : ''}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Revenue ({bookings.filter(b => b.payment_status === 'paid').length} paid)
          </Button>
        </div>

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <BookingCalendar 
                bookings={bookings} 
                onDateClick={(day, dayBookings) => {
                  setSelectedDay(day);
                  setSelectedDateBookings(dayBookings);
                }}
              />
            </div>
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {selectedDay ? `Bookings for Day ${selectedDay}` : 'Select a Date'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedDateBookings ? (
                    <p className="text-gray-500 text-center py-8">Click on a date to view bookings</p>
                  ) : selectedDateBookings.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No bookings for this date</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {selectedDateBookings.map((booking) => (
                        <div key={booking.booking_id} className={`border rounded-lg p-3 ${
                          booking.booking_status === 'completed' ? 'bg-purple-50 border-purple-200' :
                          booking.booking_status === 'confirmed' ? 'bg-green-50 border-green-200' :
                          'bg-yellow-50 border-yellow-200'
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-gray-900">{booking.name}</p>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              booking.booking_status === 'completed' ? 'bg-purple-100 text-purple-800' :
                              booking.booking_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {booking.booking_status}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <p><Package className="w-3 h-3 inline mr-1" />{booking.package_name}</p>
                            <p><Phone className="w-3 h-3 inline mr-1" />{booking.phone}</p>
                            <p><MapPin className="w-3 h-3 inline mr-1" />{booking.address}</p>
                            <p className="font-semibold text-emerald-600">
                              <DollarSign className="w-3 h-3 inline" />{booking.price}
                              {booking.payment_status === 'paid' && <span className="ml-2 text-xs bg-green-100 text-green-800 px-1 rounded">PAID</span>}
                            </p>
                          </div>
                          <div className="flex gap-2 mt-2">
                            {booking.payment_status !== 'paid' && (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs h-7" onClick={() => updatePaymentStatus(booking.booking_id, 'paid')}>
                                Mark Paid
                              </Button>
                            )}
                            {booking.booking_status === 'confirmed' && (
                              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-xs h-7" onClick={() => updateBookingStatus(booking.booking_id, 'completed')}>
                                Complete
                              </Button>
                            )}
                            {booking.booking_status === 'pending_payment' && (
                              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => updateBookingStatus(booking.booking_id, 'confirmed')}>
                                Confirm
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Quotes */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Quotes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {quotes.slice(0, 5).map((quote) => (
                    <div key={quote.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{quote.name}</p>
                          <p className="text-sm text-gray-600">{quote.email}</p>
                          <p className="text-sm text-gray-500">{quote.phone}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          quote.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                          quote.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                          'bg-green-100 text-green-800'
                        }`}>
                          {quote.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Bookings */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {bookings.slice(0, 5).map((booking) => (
                    <div key={booking.booking_id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-gray-900">{booking.name}</p>
                          <p className="text-sm text-gray-600">{booking.package_name}</p>
                          <p className="text-sm text-emerald-600 font-semibold">${booking.price}</p>
                        </div>
                        <div className="text-right">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            booking.booking_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            booking.booking_status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {booking.booking_status}
                          </span>
                          <p className="text-sm text-gray-500 mt-1">{booking.service_date}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'quotes' && (
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {quotes.map((quote) => (
                  <div key={quote.id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <p className="font-semibold text-gray-900 text-lg">{quote.name}</p>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            quote.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                            quote.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {quote.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{quote.phone}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            <span>{quote.email}</span>
                          </div>
                          {quote.address && (
                            <div className="flex items-center space-x-2">
                              <MapPin className="w-4 h-4 text-gray-400" />
                              <span>{quote.address}</span>
                            </div>
                          )}
                        </div>
                        {quote.message && (
                          <p className="text-gray-600 mt-2 text-sm">{quote.message}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(quote.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        {quote.status === 'new' && (
                          <Button size="sm" variant="outline" onClick={() => updateQuoteStatus(quote.id, 'contacted')}>
                            Mark Contacted
                          </Button>
                        )}
                        {quote.status !== 'converted' && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateQuoteStatus(quote.id, 'converted')}>
                            Mark Converted
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" onClick={() => deleteQuote(quote.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {activeTab === 'bookings' && (
          <Card>
            <CardContent className="p-6">
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <div key={booking.booking_id} className="border rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <p className="font-semibold text-gray-900 text-lg">{booking.name}</p>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            booking.booking_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            booking.booking_status === 'completed' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {booking.booking_status}
                          </span>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {booking.payment_status === 'paid' ? 'PAID' : 'Payment Pending'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <Package className="w-4 h-4 text-gray-400" />
                            <span>{booking.package_name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <CalendarIcon className="w-4 h-4 text-gray-400" />
                            <span>{booking.service_date}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span>{booking.phone}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <DollarSign className="w-4 h-4 text-gray-400" />
                            <span className="font-semibold text-emerald-600">${booking.price}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 mt-2 text-sm">
                          <MapPin className="w-4 h-4 text-gray-400" />
                          <span>{booking.address}</span>
                        </div>
                        {booking.notes && (
                          <p className="text-gray-600 mt-2 text-sm">Notes: {booking.notes}</p>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 ml-4">
                        {booking.payment_status !== 'paid' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updatePaymentStatus(booking.booking_id, 'paid')}>
                            <DollarSign className="w-4 h-4 mr-1" />
                            Mark Paid
                          </Button>
                        )}
                        {booking.booking_status === 'confirmed' && (
                          <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => updateBookingStatus(booking.booking_id, 'completed')}>
                            Mark Completed
                          </Button>
                        )}
                        {booking.booking_status === 'pending_payment' && (
                          <Button size="sm" variant="outline" onClick={() => updateBookingStatus(booking.booking_id, 'confirmed')}>
                            Confirm Booking
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" onClick={() => deleteBooking(booking.booking_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completed Jobs Tab */}
        {activeTab === 'completed' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center">
                <CheckCircle className="w-5 h-5 mr-2 text-purple-600" />
                Completed Jobs
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {bookings.filter(b => b.booking_status === 'completed').length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No completed jobs yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.filter(b => b.booking_status === 'completed').map((booking) => (
                    <div key={booking.booking_id} className="border rounded-lg p-4 bg-purple-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-4 mb-2">
                            <p className="font-semibold text-gray-900 text-lg">{booking.name}</p>
                            <span className="px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800">
                              COMPLETED
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                            }`}>
                              {booking.payment_status === 'paid' ? '✓ PAID' : 'Payment Pending'}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center space-x-2">
                              <Package className="w-4 h-4 text-gray-400" />
                              <span>{booking.package_name}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <CalendarIcon className="w-4 h-4 text-gray-400" />
                              <span>{booking.service_date}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Phone className="w-4 h-4 text-gray-400" />
                              <span>{booking.phone}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <DollarSign className="w-4 h-4 text-gray-400" />
                              <span className="font-semibold text-emerald-600">${booking.price}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 mt-2 text-sm">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            <span>{booking.address}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 ml-4">
                          {booking.payment_status !== 'paid' && (
                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updatePaymentStatus(booking.booking_id, 'paid')}>
                              <DollarSign className="w-4 h-4 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" onClick={() => deleteBooking(booking.booking_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            {/* Revenue Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-700">Revenue Collected</p>
                      <p className="text-3xl font-bold text-green-800">
                        ${bookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + b.price, 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-green-600">{bookings.filter(b => b.payment_status === 'paid').length} paid bookings</p>
                    </div>
                    <div className="bg-green-200 p-3 rounded-full">
                      <DollarSign className="w-6 h-6 text-green-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-orange-700">Pending Revenue</p>
                      <p className="text-3xl font-bold text-orange-800">
                        ${bookings.filter(b => b.payment_status === 'pending').reduce((sum, b) => sum + b.price, 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-orange-600">{bookings.filter(b => b.payment_status === 'pending').length} pending payments</p>
                    </div>
                    <div className="bg-orange-200 p-3 rounded-full">
                      <Clock className="w-6 h-6 text-orange-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-700">Total Revenue (All)</p>
                      <p className="text-3xl font-bold text-blue-800">
                        ${bookings.reduce((sum, b) => sum + b.price, 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-blue-600">{bookings.length} total bookings</p>
                    </div>
                    <div className="bg-blue-200 p-3 rounded-full">
                      <DollarSign className="w-6 h-6 text-blue-700" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Paid Bookings List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-green-600" />
                  Paid Bookings
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {bookings.filter(b => b.payment_status === 'paid').length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No paid bookings yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bookings.filter(b => b.payment_status === 'paid').map((booking) => (
                      <div key={booking.booking_id} className="border rounded-lg p-4 bg-green-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-4 mb-2">
                              <p className="font-semibold text-gray-900 text-lg">{booking.name}</p>
                              <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                ✓ PAID
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                booking.booking_status === 'completed' ? 'bg-purple-100 text-purple-800' :
                                booking.booking_status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {booking.booking_status}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                              <div className="flex items-center space-x-2">
                                <Package className="w-4 h-4 text-gray-400" />
                                <span>{booking.package_name}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <CalendarIcon className="w-4 h-4 text-gray-400" />
                                <span>{booking.service_date}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <span>{booking.email}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <DollarSign className="w-4 h-4 text-green-600" />
                                <span className="font-bold text-green-600 text-lg">${booking.price}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            {booking.booking_status === 'confirmed' && (
                              <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={() => updateBookingStatus(booking.booking_id, 'completed')}>
                                Mark Completed
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 border-red-200" onClick={() => deleteBooking(booking.booking_id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
